import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { selectUiSettings } from '@App/app/auth/store/auth/selectors/auth.selectors';
import { AppTheme, ThemePreference } from '@App/app/entities/users/user';
import { updateTheme } from '@App/app/pages/viewer/store/color-presets-store/actions/ui-settings.actions';
import { NbThemeService } from '@nebular/theme';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

export { AppTheme, ThemePreference };

export const THEME_STORAGE_KEY = 'skyportal.theme';
export const DARK_MODE_CLASS = 'dark-mode';
export const DEFAULT_PREFERENCE: ThemePreference = 'system';

const NEBULAR_THEME_NAMES: Record<AppTheme, string> = {
  light: 'skyportal-light',
  dark: 'skyportal-dark',
};

const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _preference$ = new BehaviorSubject<ThemePreference>(DEFAULT_PREFERENCE);
  /** The user's chosen preference: `light`, `dark`, or `system`. */
  readonly preference$: Observable<ThemePreference> = this._preference$.asObservable();

  private readonly _theme$ = new BehaviorSubject<AppTheme>('light');
  /** The concrete theme currently applied (`system` resolved against the OS). */
  readonly theme$: Observable<AppTheme> = this._theme$.asObservable();

  private serverWatchSubscription: Subscription | null = null;

  constructor(
    private nbThemeService: NbThemeService,
    @Inject(DOCUMENT) private document: Document,
    @Optional() private store: Store | null = null,
  ) {}

  initialize(): void {
    this.applyPreference(this.resolvePreference(), { persist: false });
    this.watchOsPreference();
    this.watchServerSettings();
  }

  /** The concrete theme currently applied. */
  get current(): AppTheme {
    return this._theme$.value;
  }

  /** The user's chosen preference. */
  get preference(): ThemePreference {
    return this._preference$.value;
  }

  /** Resolve a stored preference to a concrete theme, consulting the OS for `system`. */
  resolveTheme(preference: ThemePreference): AppTheme {
    if (preference === 'light' || preference === 'dark') {
      return preference;
    }
    return this.prefersDark() ? 'dark' : 'light';
  }

  /** The concrete theme that would be applied on a fresh load. */
  resolveInitialTheme(): AppTheme {
    return this.resolveTheme(this.resolvePreference());
  }

  resolvePreference(): ThemePreference {
    try {
      const cached = this.window?.localStorage.getItem(THEME_STORAGE_KEY);
      if (isThemePreference(cached)) {
        return cached;
      }
    } catch {
      // Ignore — fall through to the default.
    }
    return DEFAULT_PREFERENCE;
  }

  setPreference(
    preference: ThemePreference,
    opts: { persist?: boolean; userInitiated?: boolean } = {},
  ): void {
    const { persist = true, userInitiated = true } = opts;
    this.applyPreference(preference, { persist });
    if (userInitiated) {
      this.store?.dispatch(updateTheme({ theme: preference }));
    }
  }

  /** Explicit light/dark setter, kept for callers that don't expose `system`. */
  setTheme(theme: AppTheme, opts: { persist?: boolean; userInitiated?: boolean } = {}): void {
    this.setPreference(theme, opts);
  }

  toggle(): void {
    this.setPreference(this.current === 'dark' ? 'light' : 'dark');
  }

  private applyPreference(preference: ThemePreference, opts: { persist: boolean }): void {
    this._preference$.next(preference);
    this.applyTheme(this.resolveTheme(preference));
    if (opts.persist) {
      try {
        this.window?.localStorage.setItem(THEME_STORAGE_KEY, preference);
      } catch {
        // Non-fatal.
      }
    }
  }

  private applyTheme(theme: AppTheme): void {
    this.nbThemeService.changeTheme(NEBULAR_THEME_NAMES[theme]);
    this.document.documentElement.classList.toggle(DARK_MODE_CLASS, theme === 'dark');
    this._theme$.next(theme);
  }

  private prefersDark(): boolean {
    return !!this.window?.matchMedia?.('(prefers-color-scheme: dark)').matches;
  }

  private get window(): (Window & typeof globalThis) | null {
    return this.document.defaultView;
  }

  private watchOsPreference(): void {
    const media = this.window?.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    media.addEventListener('change', (event) => {
      // Only follow the OS while the user is on the `system` preference.
      if (this.preference !== 'system') return;
      this.applyTheme(event.matches ? 'dark' : 'light');
    });
  }

  private watchServerSettings(): void {
    if (!this.store || this.serverWatchSubscription) return;
    this.serverWatchSubscription = this.store
      .select(selectUiSettings)
      .pipe(
        map((settings) => settings?.theme),
        filter(isThemePreference),
        distinctUntilChanged(),
      )
      .subscribe((preference) => {
        if (preference === this.preference) return;
        // Server-provided value — apply locally without dispatching back.
        this.applyPreference(preference, { persist: true });
      });
  }
}
